import { useEffect, useRef, useState } from "react";
import { Handle, NodeResizer, Position } from "@xyflow/react";
import { useDispatch, useSelector } from "react-redux";
import { ungroupNode, updateNode } from "../../utils/flowSlice";
import { MdTextFields } from "react-icons/md";
import { FaRegObjectUngroup } from "react-icons/fa6";

const TextNode = ({ data, isConnectable }) => {
  const [noWheelClassName, setNoWheelClassName] = useState("nowheel");
  const textNodeRef = useRef(null);
  const [isEditable, setIsEditable] = useState(false);
  const [dimensions, setDimensions] = useState(
    data.dimensions || { width: 300, height: 150 }
  );
  const dispatch = useDispatch();
  const [script, setScript] = useState(data.script || "");

  const nodes = useSelector((store) => store.flow.nodes);
  const currentNode = nodes.find((node) => node.id === data.id);
  const isGrouped = !!currentNode ? !!currentNode.parentId : false;
  const unGroup = () =>
    dispatch(
      ungroupNode({
        id: data.id,
      })
    );
  const handleResize = (event, params) => {
    setDimensions({ width: params.width, height: params.height });
  };
  const handleClick = () => {
    setIsEditable(true); // Enable editing on double-click
  };

  const handleBlur = (e) => {
    setIsEditable(false); // Disable editing when the textarea loses focus

    dispatch(
      updateNode({
        id: data.id,
        data: {
          ...data,
          script: script,
          dimensions: dimensions,
        },
      })
    );
  };

  useEffect(() => {
    const textNodeElement = textNodeRef.current;

    // Ensure textNodeElement is not null before adding the event listener
    if (textNodeElement) {
      const handleWheel = (event) => {
        if (event.ctrlKey) {
          if (noWheelClassName === "nowheel") event.preventDefault();
          textNodeElement.className = "diswheel";
          setNoWheelClassName("disnowheel");
        } else {
          if (noWheelClassName === "disnowheel") event.preventDefault();
          textNodeElement.className = "nowheel";
          setNoWheelClassName("nowheel");
        }
      };

      // Add the wheel event listener without the passive option
      textNodeElement.addEventListener("wheel", handleWheel, {
        passive: false,
      });

      // Clean up the event listener when the component unmounts
      return () => {
        textNodeElement.removeEventListener("wheel", handleWheel);
      };
    }
  }, []);

  return (
    <div
      ref={textNodeRef}
      style={{
        width: dimensions.width,
        height: dimensions.height,
        position: "relative",
      }}
      className={noWheelClassName}
    >
      <NodeResizer
        top-right
        onResize={handleResize}
        color="#F7F9FB"
        minHeight={100}
        minWidth={240}
        isVisible={true} // Hides default corner controls
      />
      <Handle
        type="source"
        position={Position.Right}
        isConnectable={isConnectable}
        style={{
          width: "20px", // Default width
          height: "20px", // Default height
          background: "white", // Default background color
          border: "2px solid #32b5e5", // Default border color
          borderRadius: "50%", // Circular shape
          position: "absolute",
          right: "-20px", // Ensure proper alignment
          // top: "50%", // Align vertically
          transform: "translateY(-50%)", // Center the handle
          display: "flex",
          alignItems: "center",
          lineHeight: "20px", // Match the handle's height for vertical centering
          fontSize: "12px", // Ensure text size fits
          justifyContent: "center",
          transition: "transform 0.2s, background-color 0.2s, right 0.2s", // Smooth hover transition
        }}
        onMouseEnter={(e) => {
          e.target.style.transform = "translateY(-50%) scale(1.5)"; // Keep alignment and scale
          e.target.style.background = "#32b5e5"; // Change background to blue
          e.target.style.color = "white"; // Set text color to white
          e.target.innerHTML = "+"; // Add "+" symbol inside
        }}
        onMouseLeave={(e) => {
          e.target.style.transform = "translateY(-50%) scale(1)"; // Reset scale while maintaining alignment
          e.target.style.background = "white"; // Reset background to white
          e.target.style.color = ""; // Clear the text color
          e.target.innerHTML = ""; // Remove "+" symbol
        }}
      />
      <div
        className={`w-${dimensions.width} h-full border-[4px] rounded-2xl shadow-md  transition-colors duration-300 border-gray-300 focus-within:border-[#c27dcf]`}
      >
        <div
          className={`w-${dimensions.width} flex justify-between items-center text-white bg-[#f06996] px-4 py-2 rounded-t-xl`}
        >
          <div className="flex items-center space-x-2">
            {isGrouped && (
              <FaRegObjectUngroup
                size={"16"}
                className="hover:cursor-pointer"
                onClick={() => unGroup()}
              />
            )}
            <MdTextFields size={"22"} />
          </div>
        </div>

        <textarea
          placeholder=" Click to start writing..."
          style={{
            fontSize: "14px",
            width: dimensions.width - 8,
            height: dimensions.height - 58,
          }}
          className={` font-sans focus:outline-none px-4 py-4 overflow-y-auto ${
            isEditable ? "bg-white" : "bg-white cursor-pointer"
          }`}
          value={script}
          onChange={(e) => setScript(e.target.value)}
          readOnly={!isEditable} // Prevent editing unless editable
          onClick={handleClick} // Enable editing on double-click
          onBlur={handleBlur} // Disable editing on blur
        ></textarea>
      </div>
    </div>
  );
};

export default TextNode;
